import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Layout } from '../components/layout/Layout';
import { aalignAPI, marketplaceAPI, BACKEND_URL } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { 
  Search, 
  Clock, 
  X, 
  Zap,
  Target,
  DollarSign,
  Briefcase,
  Globe,
  RefreshCw,
  EyeOff,
  Send,
  Calendar,
  Loader2,
  UserPlus,
  CheckCircle,
  TrendingUp,
  ArrowRight
} from 'lucide-react';

function MarketplacePage() {
  var navigate = useNavigate();
  var auth = useAuth();
  var user = auth.user;
  var isSeller = auth.isSeller;
  var isAdmin = auth.isAdmin;
  var isAuthenticated = auth.isAuthenticated;
  var isConnectedWith = auth.isConnectedWith;
  var isPendingWith = auth.isPendingWith;
  var addPendingRequest = auth.addPendingRequest;
  
  var aalignCampaignsState = useState([]);
  var aalignCampaigns = aalignCampaignsState[0];
  var setAalignCampaigns = aalignCampaignsState[1];
  
  var aalignLoadingState = useState(false);
  var aalignLoading = aalignLoadingState[0];
  var setAalignLoading = aalignLoadingState[1];
  
  var aalignErrorState = useState(null);
  var aalignError = aalignErrorState[0];
  var setAalignError = aalignErrorState[1];
  
  // Business Request Dialog State
  var requestDialogState = useState(false);
  var requestDialogOpen = requestDialogState[0];
  var setRequestDialogOpen = requestDialogState[1];
  
  var selectedCampaignState = useState(null);
  var selectedCampaign = selectedCampaignState[0];
  var setSelectedCampaign = selectedCampaignState[1];
  
  var requestSubjectState = useState('');
  var requestSubject = requestSubjectState[0];
  var setRequestSubject = requestSubjectState[1];
  
  var requestMessageState = useState('');
  var requestMessage = requestMessageState[0];
  var setRequestMessage = requestMessageState[1];
  
  var sendingRequestState = useState(false);
  var sendingRequest = sendingRequestState[0];
  var setSendingRequest = sendingRequestState[1];
  
  // Booking Dialog State
  var bookingDialogState = useState(false);
  var bookingDialogOpen = bookingDialogState[0];
  var setBookingDialogOpen = bookingDialogState[1];
  
  var bookingCampaignState = useState(null);
  var bookingCampaign = bookingCampaignState[0];
  var setBookingCampaign = bookingCampaignState[1];
  
  var bookingLeadInputsState = useState({});
  var bookingLeadInputs = bookingLeadInputsState[0];
  var setBookingLeadInputs = bookingLeadInputsState[1];
  
  var bookingEndDateState = useState('');
  var bookingEndDate = bookingEndDateState[0];
  var setBookingEndDate = bookingEndDateState[1];
  
  var sendingBookingState = useState(false);
  var sendingBooking = sendingBookingState[0];
  var setSendingBooking = sendingBookingState[1];
  
  var loadingCampaignDetailsState = useState(false);
  var loadingCampaignDetails = loadingCampaignDetailsState[0];
  var setLoadingCampaignDetails = loadingCampaignDetailsState[1];
  
  // Already Serving Campaigns
  var bookedCampaignIdsState = useState([]);
  var bookedCampaignIds = bookedCampaignIdsState[0];
  var setBookedCampaignIds = bookedCampaignIdsState[1];
  
  // Pagination
  var currentPageState = useState(1);
  var currentPage = currentPageState[0];
  var setCurrentPage = currentPageState[1];
  var campaignsPerPage = 9;
  
  // Filters
  var campaignStatusFilterState = useState('all');
  var campaignStatusFilter = campaignStatusFilterState[0];
  var setCampaignStatusFilter = campaignStatusFilterState[1];
  
  var campaignTypeFilterState = useState('all');
  var campaignTypeFilter = campaignTypeFilterState[0];
  var setCampaignTypeFilter = campaignTypeFilterState[1];
  
  var campaignSearchTextState = useState('');
  var campaignSearchText = campaignSearchTextState[0];
  var setCampaignSearchText = campaignSearchTextState[1];

  var canViewAalignCampaigns = isAuthenticated && (isSeller || isAdmin);

  // Fetch seller's booked campaigns
  useEffect(function() {
    if (isSeller) {
      marketplaceAPI.getMyBookings()
        .then(function(response) {
          if (response.data.success) {
            var bookings = response.data.data || [];
            var ids = bookings.map(function(b) {
              return (b.cmp_id || '').replace('cmp_', '');
            });
            setBookedCampaignIds(ids);
          }
        })
        .catch(function(err) {
          console.error('Failed to fetch booked campaigns:', err);
        });
    }
  }, [isSeller]);

  useEffect(function() {
    if (isAuthenticated && (user?.role === 'seller' || user?.role === 'admin')) {
      var timer = setTimeout(function() {
        fetchAalignCampaigns();
      }, 100);
      return function() { clearTimeout(timer); };
    }
  }, [isAuthenticated, user?.role]);

  function fetchAalignCampaigns() {
    var token = localStorage.getItem('auth_token');
    if (!token) {
      setAalignLoading(false);
      setAalignError('Not authenticated');
      return;
    }
    
    setAalignLoading(true);
    setAalignError(null);
    
    fetch(BACKEND_URL + '/api/aalign/campaigns', {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': 'Bearer ' + token
      }
    })
      .then(function(res) { return res.json(); })
      .then(function(data) {
        if (data.success) {
          var campaigns = data.data || [];
          var processedCampaigns = campaigns.map(function(campaign) {
            var offerings = campaign.offering || [];
            var totalTarget = 0;
            var totalBudget = 0;
            var latestUpdate = null;
            
            for (var i = 0; i < offerings.length; i++) {
              var target = parseInt(offerings[i].target) || 0;
              var cpl = parseFloat(offerings[i].cpl) || 0;
              totalTarget += target;
              totalBudget += cpl * target;
              
              if (offerings[i].last_updated) {
                var updateDate = new Date(offerings[i].last_updated);
                if (!latestUpdate || updateDate > latestUpdate) {
                  latestUpdate = updateDate;
                }
              }
            }
            
            return {
              ...campaign,
              total_target: totalTarget > 0 ? totalTarget : campaign.total_target,
              total_budget: totalBudget > 0 ? totalBudget : campaign.total_budget,
              _lastUpdated: latestUpdate
            };
          });
          
          processedCampaigns.sort(function(a, b) {
            if (!a._lastUpdated && !b._lastUpdated) return 0;
            if (!a._lastUpdated) return 1;
            if (!b._lastUpdated) return -1;
            return b._lastUpdated - a._lastUpdated;
          });
          
          setAalignCampaigns(processedCampaigns);
        } else {
          setAalignError(data.error || 'Failed to fetch campaigns');
        }
        setAalignLoading(false);
      })
      .catch(function(error) {
        console.error('fetchAalignCampaigns error:', error);
        setAalignError('Failed to connect to AALIGN');
        setAalignLoading(false);
      });
  }

  function formatBuyerId(buyerId, buyerName) {
    if (!buyerId) return 'N/A';
    var initials = '';
    if (buyerName) {
      var words = buyerName.trim().split(/\s+/);
      if (words.length >= 1) initials += words[0].charAt(0).toUpperCase();
      if (words.length >= 2) initials += words[words.length - 1].charAt(0).toUpperCase();
      else if (words.length === 1 && words[0].charAt(1)) initials += words[0].charAt(1).toUpperCase();
    }
    return initials + buyerId.substring(0, 2).toUpperCase() + buyerId.slice(-4).toUpperCase();
  }

  function isAlreadyServing(campaignId) {
    if (!campaignId) return false;
    return bookedCampaignIds.includes(campaignId);
  }

  function getFilteredCampaigns() {
    var filtered = aalignCampaigns;
    
    if (campaignSearchText) {
      var searchLower = campaignSearchText.toLowerCase();
      filtered = filtered.filter(function(campaign) {
        var name = (campaign.campaign_name || '').toLowerCase();
        var type = (campaign.type || '').toLowerCase();
        var buyerName = (campaign.buyer_name || '').toLowerCase();
        var buyerId = campaign.account_id || campaign.buyer_id || '';
        var bid = formatBuyerId(buyerId, campaign.buyer_name).toLowerCase();
        return name.includes(searchLower) || type.includes(searchLower) || buyerName.includes(searchLower) || bid.includes(searchLower);
      });
    }
    
    if (campaignStatusFilter !== 'all') {
      filtered = filtered.filter(function(campaign) {
        if (campaignStatusFilter === 'serving') return isAlreadyServing(campaign.id);
        if (campaignStatusFilter === 'available') return !isAlreadyServing(campaign.id);
        return true;
      });
    }
    
    if (campaignTypeFilter !== 'all') {
      filtered = filtered.filter(function(campaign) {
        return (campaign.type || '').toLowerCase() === campaignTypeFilter.toLowerCase();
      });
    }
    
    return filtered;
  }

  function getCampaignTypes() {
    var types = [];
    for (var i = 0; i < aalignCampaigns.length; i++) {
      var type = aalignCampaigns[i].type;
      if (type && !types.includes(type)) types.push(type);
    }
    return types;
  }

  function getPaginatedCampaigns() {
    var filtered = getFilteredCampaigns();
    var startIndex = (currentPage - 1) * campaignsPerPage;
    return filtered.slice(startIndex, startIndex + campaignsPerPage);
  }

  function getTotalPages() {
    return Math.ceil(getFilteredCampaigns().length / campaignsPerPage);
  }

  function handlePageChange(page) {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  useEffect(function() { setCurrentPage(1); }, [campaignSearchText, campaignStatusFilter, campaignTypeFilter]);

  // Business Request Handlers
  function openRequestDialog(campaign) {
    setSelectedCampaign(campaign);
    setRequestSubject('Lead Generation Partnership Request');
    setRequestMessage('Hi, I am interested in providing leads for your campaign. I have experience in the relevant industries and regions.');
    setRequestDialogOpen(true);
  }

  function closeRequestDialog() {
    setRequestDialogOpen(false);
    setSelectedCampaign(null);
    setRequestSubject('');
    setRequestMessage('');
  }

  function handleSendRequest() {
    if (!selectedCampaign || !requestSubject || !requestMessage) {
      toast.error('Please fill in all fields');
      return;
    }
    var buyerId = selectedCampaign.buyer_id || selectedCampaign.account_id;
    setSendingRequest(true);
    marketplaceAPI.sendRequest({
      to_user_id: buyerId,
      to_role: 'buyer',
      subject: requestSubject,
      message: requestMessage
    })
      .then(function(response) {
        if (response.data.status === 'success' || response.data.success) {
          toast.success('Business request sent successfully!');
          addPendingRequest(buyerId);
          closeRequestDialog();
        } else {
          toast.error(response.data.message || 'Failed to send request');
        }
      })
      .catch(function(err) {
        toast.error(err.response?.data?.detail || 'Failed to send request');
      })
      .finally(function() { setSendingRequest(false); });
  }

  // Booking Handlers
  function openBookingDialog(campaign) {
    setBookingCampaign(campaign);
    setBookingLeadInputs({});
    setBookingEndDate('');
    setBookingDialogOpen(true);
    
    if (campaign.id && !campaign.end_date) {
      setLoadingCampaignDetails(true);
      aalignAPI.getCampaignDetail(campaign.id)
        .then(function(response) {
          if (response.data.success && response.data.data) {
            setBookingCampaign(function(prev) { return { ...prev, ...response.data.data }; });
          }
        })
        .catch(function(err) { console.error('Failed to fetch campaign details:', err); })
        .finally(function() { setLoadingCampaignDetails(false); });
    }
  }

  function closeBookingDialog() {
    setBookingDialogOpen(false);
    setBookingCampaign(null);
    setBookingLeadInputs({});
    setBookingEndDate('');
  }
  
  function updateLeadInput(leadType, value) {
    setBookingLeadInputs(function(prev) { return { ...prev, [leadType]: value }; });
  }

  function handleBookCampaign() {
    if (!bookingCampaign || !bookingEndDate) {
      toast.error('Please select your delivery end date');
      return;
    }
    
    var campaignEndDate = bookingCampaign.end_date || '';
    if (!campaignEndDate) {
      toast.error('Campaign end date not available');
      return;
    }
    
    if (new Date(bookingEndDate) > new Date(campaignEndDate)) {
      toast.error('Delivery date cannot be after campaign end date');
      return;
    }
    
    var offerings = bookingCampaign.offering || bookingCampaign.offerings || [];
    var bookings = [];
    
    for (var i = 0; i < offerings.length; i++) {
      var offering = offerings[i];
      var leadType = offering.lead_type;
      var inputValue = bookingLeadInputs[leadType];
      
      if (inputValue && parseInt(inputValue) > 0) {
        var leadsCommit = parseInt(inputValue);
        var maxTarget = offering.deficit !== undefined ? offering.deficit : ((offering.target || 0) - (offering.delivered || 0));
        
        if (leadsCommit > maxTarget) {
          toast.error(leadType + ': Cannot exceed remaining target of ' + maxTarget);
          return;
        }
        
        bookings.push({
          account_id: bookingCampaign.account_id || bookingCampaign.buyer_id,
          partner_id: user?.id,
          cmp_id: bookingCampaign.id,
          lead_type: leadType,
          target: offering.target || 0,
          cpl: parseFloat(offering.cpl || offering.price || 0),
          end_date: campaignEndDate,
          leads_commit: leadsCommit,
          end_date_commit: bookingEndDate
        });
      }
    }
    
    if (bookings.length === 0) {
      toast.error('Please enter leads for at least one lead type');
      return;
    }

    setSendingBooking(true);
    marketplaceAPI.bookCampaign({ bookings: bookings })
      .then(function(response) {
        if (response.data.status === 'success' || response.data.success) {
          toast.success('Campaign booked successfully!');
          closeBookingDialog();
        } else {
          toast.error(response.data.message || 'Failed to book campaign');
        }
      })
      .catch(function(err) { toast.error(err.response?.data?.detail || 'Failed to book campaign'); })
      .finally(function() { setSendingBooking(false); });
  }

  // Calculate stats
  function getStats() {
    var totalBudget = 0;
    var totalLeads = 0;
    var servingCount = 0;
    for (var i = 0; i < aalignCampaigns.length; i++) {
      totalBudget += aalignCampaigns[i].total_budget || 0;
      totalLeads += aalignCampaigns[i].total_target || 0;
      if (isAlreadyServing(aalignCampaigns[i].id)) servingCount++;
    }
    return { totalBudget: totalBudget, totalLeads: totalLeads, servingCount: servingCount };
  }

  function formatBudget(budget) {
    if (!budget) return '$0';
    return '$' + parseFloat(budget).toLocaleString();
  }

  function formatCampaignId(id) {
    if (!id || id.length < 6) return id || 'N/A';
    return id.substring(0, 2) + '···' + id.slice(-4);
  }

  function maskCampaignName(name) {
    if (!name) return 'Campaign';
    var words = name.replace(/_/g, ' ').split(' ');
    if (words.length <= 1) return words[0].substring(0, 4) + '···';
    return words[0] + ' ' + words[1].substring(0, 3) + '···';
  }

  function renderCampaignCard(campaign, index) {
    var campaignId = campaign.id || String(index);
    var buyerId = campaign.buyer_id || campaign.account_id;
    var isConnected = buyerId ? isConnectedWith(buyerId) : false;
    var isPending = buyerId ? isPendingWith(buyerId) : false;
    var isServing = isAlreadyServing(campaignId);
    var geoList = campaign.geo || [];
    
    function renderActionButton() {
      if (isConnected) {
        return (
          <Button 
            className="w-full bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white shadow-lg shadow-orange-200"
            onClick={function(e) { e.stopPropagation(); openBookingDialog(campaign); }}
            data-testid={'book-campaign-btn-' + index}
          >
            <Calendar className="w-4 h-4 mr-2" />
            Book Campaign
          </Button>
        );
      } else if (isPending) {
        return (
          <Button variant="outline" className="w-full border-amber-300 text-amber-600 bg-amber-50" disabled>
            <Clock className="w-4 h-4 mr-2" />
            Request Pending
          </Button>
        );
      } else {
        return (
          <Button 
            variant="outline" 
            className="w-full border-orange-300 text-orange-600 hover:bg-orange-50"
            onClick={function(e) { e.stopPropagation(); openRequestDialog(campaign); }}
            data-testid={'send-request-btn-' + index}
          >
            <UserPlus className="w-4 h-4 mr-2" />
            Send Request
          </Button>
        );
      }
    }
    
    return (
      <Card 
        key={campaignId}
        className="group relative overflow-hidden border-0 shadow-md hover:shadow-xl transition-all duration-300 cursor-pointer bg-white"
        onClick={function() { navigate('/aalign/campaign/' + campaignId); }}
        data-testid={'aalign-campaign-' + index}
      >
        {/* Top accent bar */}
        <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-orange-400 via-orange-500 to-amber-500" />
        
        <CardContent className="p-0">
          {/* Header */}
          <div className="p-5 pb-4">
            <div className="flex items-start justify-between gap-3 mb-3">
              <div className="flex items-center gap-2">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-orange-100 to-amber-100 flex items-center justify-center">
                  <Zap className="w-5 h-5 text-orange-600" />
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-medium text-orange-600 bg-orange-50 px-2 py-0.5 rounded-full">
                      {campaign.type || 'Campaign'}
                    </span>
                    {campaign.status === 'Active' && (
                      <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
                    )}
                  </div>
                  <p className="text-xs text-slate-400 font-mono mt-0.5">
                    BID: {formatBuyerId(buyerId, campaign.buyer_name)}
                  </p>
                </div>
              </div>
              {isServing && (
                <Badge className="bg-emerald-100 text-emerald-700 border-0 shadow-sm">
                  <CheckCircle className="w-3 h-3 mr-1" />
                  Serving
                </Badge>
              )}
              {isConnected && !isServing && (
                <Badge className="bg-green-100 text-green-700 border-0">Connected</Badge>
              )}
              {isPending && (
                <Badge className="bg-amber-100 text-amber-700 border-0">Pending</Badge>
              )}
            </div>
            
            {/* Campaign Name (masked) */}
            <h3 className="font-semibold text-slate-800 text-lg flex items-center gap-2 mb-1">
              <EyeOff className="w-4 h-4 text-slate-400" />
              {maskCampaignName(campaign.campaign_name)}
            </h3>
            <p className="text-xs text-slate-400 font-mono">CID: {formatCampaignId(campaign.id)}</p>
          </div>

          {/* Stats Grid */}
          <div className="px-5 pb-4">
            <div className="grid grid-cols-2 gap-3">
              <div className="bg-gradient-to-br from-slate-50 to-slate-100 rounded-xl p-3">
                <div className="flex items-center gap-1.5 text-slate-500 mb-1">
                  <Target className="w-3.5 h-3.5" />
                  <span className="text-xs font-medium">Target</span>
                </div>
                <p className="text-lg font-bold text-slate-800">{campaign.total_target || 0}</p>
                <p className="text-xs text-slate-400">leads</p>
              </div>
              <div className="bg-gradient-to-br from-emerald-50 to-teal-50 rounded-xl p-3">
                <div className="flex items-center gap-1.5 text-emerald-600 mb-1">
                  <DollarSign className="w-3.5 h-3.5" />
                  <span className="text-xs font-medium">Budget</span>
                </div>
                <p className="text-lg font-bold text-emerald-700">{formatBudget(campaign.total_budget)}</p>
                <p className="text-xs text-emerald-500">total value</p>
              </div>
            </div>
          </div>

          {/* Regions */}
          {geoList.length > 0 && (
            <div className="px-5 pb-4">
              <div className="flex items-center gap-1.5 text-slate-500 mb-2">
                <Globe className="w-3.5 h-3.5" />
                <span className="text-xs font-medium">Regions</span>
              </div>
              <div className="flex flex-wrap gap-1.5">
                {geoList.slice(0, 3).map(function(geo, i) {
                  return (
                    <span key={i} className="text-xs bg-blue-50 text-blue-700 px-2 py-1 rounded-lg">
                      {geo}
                    </span>
                  );
                })}
                {geoList.length > 3 && (
                  <span className="text-xs bg-slate-100 text-slate-500 px-2 py-1 rounded-lg">
                    +{geoList.length - 3} more
                  </span>
                )}
              </div>
            </div>
          )}

          {/* Action Footer */}
          <div className="p-4 pt-0 space-y-2">
            {renderActionButton()}
            <Button 
              variant="ghost" 
              className="w-full text-slate-500 hover:text-slate-700 hover:bg-slate-50"
              onClick={function(e) { e.stopPropagation(); navigate('/aalign/campaign/' + campaignId); }}
            >
              View Details
              <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  function renderBookingOfferingsRows() {
    if (!bookingCampaign) return null;
    var offerings = bookingCampaign.offering || bookingCampaign.offerings || [];
    if (offerings.length === 0) return null;
    
    var rows = [];
    for (var idx = 0; idx < offerings.length; idx++) {
      var offering = offerings[idx];
      var cpl = offering.cpl || offering.price || 0;
      var remaining = offering.deficit !== undefined ? offering.deficit : ((offering.target || 0) - (offering.delivered || 0));
      var leadType = offering.lead_type;
      
      // Use IIFE to capture the correct leadType for each row
      rows.push(
        (function(lt, r, c, t, i) {
          return (
            <tr key={i} className="hover:bg-slate-50">
              <td className="px-3 py-2 font-medium text-slate-900">{lt}</td>
              <td className="px-3 py-2 text-right text-slate-600">${c}</td>
              <td className="px-3 py-2 text-right text-slate-600">{t}</td>
              <td className="px-3 py-2 text-right text-emerald-600 font-medium">{r}</td>
              <td className="px-3 py-2">
                <Input
                  type="number"
                  min="0"
                  max={r}
                  placeholder="0"
                  className="w-20 h-8 text-center"
                  value={bookingLeadInputs[lt] || ''}
                  onChange={function(e) { updateLeadInput(lt, e.target.value); }}
                  data-testid={'booking-input-' + lt}
                />
              </td>
            </tr>
          );
        })(leadType, remaining, cpl, offering.target || 0, idx)
      );
    }
    return rows;
  }

  function renderSkeletons() {
    var items = [];
    for (var i = 0; i < 6; i++) {
      items.push(
        <Card key={i} className="border-0 shadow-md">
          <CardContent className="p-5 space-y-4">
            <div className="flex items-center gap-3">
              <Skeleton className="h-10 w-10 rounded-xl" />
              <div className="space-y-2">
                <Skeleton className="h-4 w-24" />
                <Skeleton className="h-3 w-16" />
              </div>
            </div>
            <Skeleton className="h-6 w-40" />
            <div className="grid grid-cols-2 gap-3">
              <Skeleton className="h-20 rounded-xl" />
              <Skeleton className="h-20 rounded-xl" />
            </div>
            <Skeleton className="h-10 w-full rounded-lg" />
          </CardContent>
        </Card>
      );
    }
    return items;
  }

  function renderAalignContent() {
    if (aalignLoading && aalignCampaigns.length === 0) {
      return <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">{renderSkeletons()}</div>;
    }

    if (aalignError) {
      return (
        <div className="text-center py-16">
          <div className="w-20 h-20 rounded-2xl bg-red-100 flex items-center justify-center mx-auto mb-4">
            <X className="w-10 h-10 text-red-500" />
          </div>
          <h3 className="text-lg font-semibold text-slate-800 mb-2">Connection Error</h3>
          <p className="text-slate-500 mb-6">{aalignError}</p>
          <Button onClick={fetchAalignCampaigns} className="bg-orange-500 hover:bg-orange-600">
            <RefreshCw className="w-4 h-4 mr-2" />
            Try Again
          </Button>
        </div>
      );
    }

    if (aalignCampaigns.length === 0) {
      return (
        <div className="text-center py-16">
          <div className="w-20 h-20 rounded-2xl bg-orange-100 flex items-center justify-center mx-auto mb-4">
            <Zap className="w-10 h-10 text-orange-500" />
          </div>
          <h3 className="text-lg font-semibold text-slate-800 mb-2">No Campaigns Available</h3>
          <p className="text-slate-500">Check back later for new buyer campaigns</p>
        </div>
      );
    }

    var filteredCampaigns = getFilteredCampaigns();
    if (filteredCampaigns.length === 0) {
      return (
        <div className="text-center py-16">
          <div className="w-20 h-20 rounded-2xl bg-slate-100 flex items-center justify-center mx-auto mb-4">
            <Search className="w-10 h-10 text-slate-400" />
          </div>
          <h3 className="text-lg font-semibold text-slate-800 mb-2">No Matching Campaigns</h3>
          <p className="text-slate-500">Try adjusting your filters</p>
        </div>
      );
    }

    var paginatedCampaigns = getPaginatedCampaigns();
    var cards = [];
    for (var i = 0; i < paginatedCampaigns.length; i++) {
      cards.push(renderCampaignCard(paginatedCampaigns[i], i));
    }
    return <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">{cards}</div>;
  }

  function renderHeroHeader() {
    return (
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-slate-900 via-slate-800 to-slate-900 p-8 mb-8">
        <div className="absolute inset-0 opacity-30">
          <div className="absolute top-0 left-0 w-40 h-40 bg-orange-500 rounded-full blur-3xl opacity-20" />
          <div className="absolute bottom-0 right-0 w-60 h-60 bg-amber-500 rounded-full blur-3xl opacity-10" />
        </div>
        <div className="relative">
          <div className="flex items-center gap-2 mb-3">
            <span className="px-3 py-1 text-xs font-medium bg-orange-500/20 text-orange-400 rounded-full border border-orange-500/30">
              B2B Lead Marketplace
            </span>
          </div>
          <h1 className="text-3xl md:text-4xl font-bold text-white mb-2">
            Browse Campaigns
          </h1>
          <p className="text-slate-400 max-w-xl">
            Browse active campaigns from AALIGN buyers. Send requests to connect and start delivering high-quality leads.
          </p>
        </div>
        <div className="absolute right-8 top-1/2 -translate-y-1/2 hidden lg:block">
          <div className="w-32 h-32 rounded-full bg-gradient-to-br from-orange-500/20 to-amber-500/10 blur-2xl" />
        </div>
      </div>
    );
  }

  var stats = getStats();

  return (
    <Layout>
      <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
        <div className="container mx-auto px-4 lg:px-8 py-8" data-testid="marketplace-page">
          
          {/* Hero Header */}
          {renderHeroHeader()}

          {/* Stats Bar */}
          {canViewAalignCampaigns && aalignCampaigns.length > 0 && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
              <div className="bg-white rounded-xl p-4 shadow-sm border border-slate-100">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-orange-100 flex items-center justify-center">
                    <Zap className="w-5 h-5 text-orange-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-slate-800">{aalignCampaigns.length}</p>
                    <p className="text-xs text-slate-500">Active Campaigns</p>
                  </div>
                </div>
              </div>
              <div className="bg-white rounded-xl p-4 shadow-sm border border-slate-100">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                    <Target className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-slate-800">{stats.totalLeads.toLocaleString()}</p>
                    <p className="text-xs text-slate-500">Total Leads Needed</p>
                  </div>
                </div>
              </div>
              <div className="bg-white rounded-xl p-4 shadow-sm border border-slate-100">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center">
                    <DollarSign className="w-5 h-5 text-emerald-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-emerald-600">{formatBudget(stats.totalBudget)}</p>
                    <p className="text-xs text-slate-500">Total Budget</p>
                  </div>
                </div>
              </div>
              <div className="bg-white rounded-xl p-4 shadow-sm border border-slate-100">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center">
                    <TrendingUp className="w-5 h-5 text-purple-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-slate-800">{stats.servingCount}</p>
                    <p className="text-xs text-slate-500">You're Serving</p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Filters */}
          {canViewAalignCampaigns && (
            <div className="bg-white rounded-xl shadow-sm border border-slate-100 p-4 mb-6">
              <div className="flex flex-wrap items-center gap-3">
                <div className="flex-1 min-w-[250px]">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      placeholder="Search campaigns by name, type, or BID..."
                      value={campaignSearchText}
                      onChange={function(e) { setCampaignSearchText(e.target.value); }}
                      className="pl-10 border-slate-200 focus:border-orange-300 focus:ring-orange-200"
                      data-testid="campaign-search-input"
                    />
                  </div>
                </div>
                <Select value={campaignStatusFilter} onValueChange={setCampaignStatusFilter}>
                  <SelectTrigger className="w-[150px] border-slate-200" data-testid="status-filter">
                    <SelectValue placeholder="Status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Status</SelectItem>
                    <SelectItem value="available">Available</SelectItem>
                    <SelectItem value="serving">Already Serving</SelectItem>
                  </SelectContent>
                </Select>
                <Select value={campaignTypeFilter} onValueChange={setCampaignTypeFilter}>
                  <SelectTrigger className="w-[150px] border-slate-200" data-testid="type-filter">
                    <SelectValue placeholder="Type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Types</SelectItem>
                    {getCampaignTypes().map(function(type) {
                      return <SelectItem key={type} value={type}>{type}</SelectItem>;
                    })}
                  </SelectContent>
                </Select>
                <Button 
                  variant="outline" 
                  size="icon"
                  onClick={fetchAalignCampaigns} 
                  disabled={aalignLoading}
                  className="border-slate-200"
                >
                  <RefreshCw className={'w-4 h-4 ' + (aalignLoading ? 'animate-spin' : '')} />
                </Button>
                {(campaignSearchText || campaignStatusFilter !== 'all' || campaignTypeFilter !== 'all') && (
                  <Button 
                    variant="ghost" 
                    size="sm"
                    onClick={function() {
                      setCampaignSearchText('');
                      setCampaignStatusFilter('all');
                      setCampaignTypeFilter('all');
                    }}
                    className="text-slate-500"
                  >
                    <X className="w-4 h-4 mr-1" />
                    Clear
                  </Button>
                )}
              </div>
              
              {/* Results count */}
              <div className="mt-3 pt-3 border-t border-slate-100">
                <p className="text-sm text-slate-500">
                  Showing {getPaginatedCampaigns().length} of {getFilteredCampaigns().length} campaigns
                  {getFilteredCampaigns().length !== aalignCampaigns.length && ' (filtered)'}
                </p>
              </div>
            </div>
          )}

          {/* Campaign Grid */}
          {canViewAalignCampaigns ? (
            <>
              {renderAalignContent()}
              
              {/* Pagination */}
              {getTotalPages() > 1 && (
                <div className="flex items-center justify-center gap-2 mt-8">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={function() { handlePageChange(currentPage - 1); }}
                    disabled={currentPage === 1}
                    className="border-slate-200"
                  >
                    Previous
                  </Button>
                  <div className="flex items-center gap-1">
                    {Array.from({ length: getTotalPages() }, function(_, i) {
                      var page = i + 1;
                      if (page === 1 || page === getTotalPages() || (page >= currentPage - 1 && page <= currentPage + 1)) {
                        return (
                          <Button
                            key={page}
                            variant={currentPage === page ? 'default' : 'outline'}
                            size="sm"
                            className={currentPage === page ? 'bg-orange-500 hover:bg-orange-600' : 'border-slate-200'}
                            onClick={function() { handlePageChange(page); }}
                          >
                            {page}
                          </Button>
                        );
                      } else if (page === currentPage - 2 || page === currentPage + 2) {
                        return <span key={page} className="px-2 text-slate-400">···</span>;
                      }
                      return null;
                    })}
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={function() { handlePageChange(currentPage + 1); }}
                    disabled={currentPage === getTotalPages()}
                    className="border-slate-200"
                  >
                    Next
                  </Button>
                </div>
              )}
            </>
          ) : (
            <div className="text-center py-16">
              <div className="w-20 h-20 rounded-2xl bg-slate-100 flex items-center justify-center mx-auto mb-4">
                <Briefcase className="w-10 h-10 text-slate-400" />
              </div>
              <h3 className="text-lg font-semibold text-slate-800 mb-2">Seller Access Required</h3>
              <p className="text-slate-500">Log in as a seller to view buyer campaigns</p>
            </div>
          )}
        </div>
      </div>

      {/* Business Request Dialog */}
      <Dialog open={requestDialogOpen} onOpenChange={setRequestDialogOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserPlus className="w-5 h-5 text-orange-500" />
              Send Connection Request
            </DialogTitle>
            <DialogDescription>
              Request to connect with this buyer and start delivering leads for their campaign.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="request-subject">Subject</Label>
              <Input
                id="request-subject"
                value={requestSubject}
                onChange={function(e) { setRequestSubject(e.target.value); }}
                placeholder="Enter subject"
                data-testid="request-subject-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="request-message">Message</Label>
              <Textarea
                id="request-message"
                value={requestMessage}
                onChange={function(e) { setRequestMessage(e.target.value); }}
                placeholder="Introduce yourself and your lead generation capabilities..."
                rows={4}
                data-testid="request-message-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={closeRequestDialog} disabled={sendingRequest}>
              Cancel
            </Button>
            <Button onClick={handleSendRequest} disabled={sendingRequest} className="bg-orange-500 hover:bg-orange-600" data-testid="confirm-send-request-btn">
              {sendingRequest ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Sending...</>
              ) : (
                <><Send className="w-4 h-4 mr-2" />Send Request</>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Booking Dialog */}
      <Dialog open={bookingDialogOpen} onOpenChange={setBookingDialogOpen}>
        <DialogContent className="sm:max-w-[550px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Calendar className="w-5 h-5 text-orange-500" />
              Book Campaign
            </DialogTitle>
            <DialogDescription>
              Enter the number of leads you want to deliver for each lead type.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {bookingCampaign && (
              <div className="bg-orange-50 rounded-lg p-3 flex items-center justify-between">
                <div className="text-sm text-slate-600">Campaign End Date</div>
                <div className="font-semibold text-slate-900">
                  {loadingCampaignDetails ? (
                    <span className="flex items-center gap-1 text-slate-400">
                      <Loader2 className="w-3 h-3 animate-spin" />Loading...
                    </span>
                  ) : bookingCampaign.end_date ? (
                    new Date(bookingCampaign.end_date).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })
                  ) : 'Not specified'}
                </div>
              </div>
            )}

            {bookingCampaign && (bookingCampaign.offering || bookingCampaign.offerings) && (bookingCampaign.offering || bookingCampaign.offerings).length > 0 ? (
              <div className="border rounded-lg overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-slate-100">
                    <tr>
                      <th className="px-3 py-2 text-left font-medium text-slate-700">Lead Type</th>
                      <th className="px-3 py-2 text-right font-medium text-slate-700">CPL</th>
                      <th className="px-3 py-2 text-right font-medium text-slate-700">Target</th>
                      <th className="px-3 py-2 text-right font-medium text-slate-700">Remaining</th>
                      <th className="px-3 py-2 text-center font-medium text-slate-700">You Commit</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">{renderBookingOfferingsRows()}</tbody>
                </table>
              </div>
            ) : (
              <div className="text-center py-4 text-slate-500">
                {loadingCampaignDetails ? 'Loading offerings...' : 'No lead types available'}
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="booking-end-date">Your Delivery Date</Label>
              <Input
                id="booking-end-date"
                type="date"
                value={bookingEndDate}
                max={bookingCampaign?.end_date || ''}
                onChange={function(e) { setBookingEndDate(e.target.value); }}
                data-testid="booking-end-date-input"
              />
              <p className="text-xs text-slate-500">
                {bookingCampaign?.end_date 
                  ? 'Must be on or before ' + bookingCampaign.end_date
                  : 'When you commit to deliver the leads by'}
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={closeBookingDialog} disabled={sendingBooking}>Cancel</Button>
            <Button onClick={handleBookCampaign} disabled={sendingBooking} className="bg-orange-500 hover:bg-orange-600" data-testid="confirm-booking-btn">
              {sendingBooking ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Booking...</>
              ) : (
                <><Calendar className="w-4 h-4 mr-2" />Confirm Booking</>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}

export default MarketplacePage;
